import turfBooleanPointInPolygon from "@turf/boolean-point-in-polygon";
import turfContains from '@turf/boolean-contains';
import turfLineSplit from '@turf/line-split';
import turfFlatten from '@turf/flatten';
import turfUnion from '@turf/union';
import turfLength from '@turf/length';
import turfAlong from '@turf/along';
import turfDifference from '@turf/difference';
import turfPolygonToLine from "@turf/polygon-to-line";
import * as turf from '@turf/turf';
import { vegetationAreas } from "../constants/vegetationAreas";

const SUCCESS_PROBALILITY = {
  wood: 0.65,
  scrub: 0.85,
  crop: 0.95,
  grass: 0.99,
  other: 1.00,
};

const RUNNING_TIME = {
  wood: 115,
  scrub: 45,
  crop: 15,
  grass: 10,
  other: 0,
};

const WATER_VOLUME_MODIFIER = 0.225;
const RISK_ORDER = { low: 0, elevated: 1, high: 2 };
const RISK_MESSAGE = {
  low: {
    color: "#34BF90",
    risk: "Low",
    text: "Risk parameters are evolving constantly, we therefore recommend reaching out to our team for an early risk assessment."
  },
  elevated: {
    color: "#E9BB25",
    risk: "Elevated",
    text: "We advise obtaining a detailed risk analysis with an expert of our team."
  },
  high: {
    color: "#E33022",
    risk: "High",
    text: "We strongly encourage a detailed risk analysis with an expert of our team."
  }
};

export function calculatePointsInZones(map, draw) {
  const points = draw.getAll().features
    .filter(f => f.geometry.type === 'Point');

  const buffers = Object.keys(map.getStyle().sources)
    .filter(id => id.startsWith('buffer-'))
    .map(id => map.getSource(id)?._data)
    .filter(Boolean);

  if (!points.length || !buffers.length) return 0;

  let score = 0;
  points.forEach(p => {
    const hit = buffers.some(b => turfBooleanPointInPolygon(p, b));
    if (hit) score = 5;
  });
  return score;
}

export function getSplitedLinesByLandcover(map, feature) {
  const type = feature.geometry?.type;

  const polygonToLine = (type === "Polygon" || type === "MultiPolygon") ? turfPolygonToLine(feature) : feature;
  const landcoverFeatures = map.queryRenderedFeatures({ layers: ['landcover-2'] });

  const grouped = landcoverFeatures.reduce((acc, f) => {
    const c = f?.properties?.class || 'other';
    if (!acc[c]) acc[c] = [];
    acc[c].push(f);
    return acc;
  }, {});

  const united = Object.values(grouped).map((arr) => {
    return arr.reduce((acc, f) => acc ? { ...turfUnion(acc, f), properties: f.properties } : f, null);
  });

  const cropped = united.map((f) => {
    const others = united.filter(o => o && o.properties?.class !== f.properties?.class);
    return others.reduce((acc, o) => turfDifference(acc, o) ?? acc, f);
  });

  return cropped.reduce((lines, landcover) => {
    return lines.flatMap(line => {
      if (line.properties?.class) return line;
      const splitted = turfLineSplit(line, landcover).features;
      const parts = (splitted.length > 0 ? splitted : [line]).map(seg => {
        const polygons = landcover.geometry.type === 'MultiPolygon' ? turfFlatten(landcover).features : [landcover];
        const len = turfLength(seg);
        const mid = turfAlong(seg, len / 2);
        if (polygons.some(p => turfContains(p, mid)) && seg.properties?.class !== 'snow') {
          seg.properties = { ...(seg.properties || {}), class: landcover.properties.class };
        }
        return seg;
      });
      return parts;
    });
  }, [polygonToLine]);
}

function getGroupedLineDistance(map, lotPerimeter) {
  const splittedLine = getSplitedLinesByLandcover(map, lotPerimeter);

  return splittedLine.reduce((acc, f) => {
    const c = f.properties?.class || 'other';
    acc[c] = (acc[c] || 0) + turfLength(f) * 1000;
    return acc;
  }, {});
}

export function calculatePolygonLength(feature) {
  const type = feature.geometry?.type;
  const line = (type === "Polygon" || type === "MultiPolygon") ? turfPolygonToLine(feature) : feature;

  return turfLength(line) * 1000;
}

function calculateRunningTime(groupedLineDistance) {
  return (() => {
    if (groupedLineDistance.wood) return RUNNING_TIME.wood;
    if (groupedLineDistance.scrub) return RUNNING_TIME.scrub;
    if (groupedLineDistance.crop) return RUNNING_TIME.crop;
    if (groupedLineDistance.grass) return RUNNING_TIME.grass;
    return RUNNING_TIME.other;
  })();
}

function calculateWaterVolume(lotPerimeter, groupedLineDistance) {
  const distance = calculatePolygonLength(lotPerimeter);
  const runningTime = calculateRunningTime(groupedLineDistance);

  return WATER_VOLUME_MODIFIER * distance * runningTime;
}

function calculateSuccessProbability(map, lotPerimeter) {
  const splittedLine = getSplitedLinesByLandcover(map, lotPerimeter);
  const distance = calculatePolygonLength(lotPerimeter);

  return parseFloat(splittedLine.reduce((acc, f) => {
    const c = f.properties?.class || 'other';
    const chunk = turfLength(f) * 1000;
    if (c === 'wood') return acc + (chunk * SUCCESS_PROBALILITY.wood / distance);
    if (c === 'scrub') return acc + (chunk * SUCCESS_PROBALILITY.scrub / distance);
    if (c === 'crop') return acc + (chunk * SUCCESS_PROBALILITY.crop / distance);
    if (c === 'grass') return acc + (chunk * SUCCESS_PROBALILITY.grass / distance);
    return acc + (chunk * SUCCESS_PROBALILITY.other / distance);
  }, 0).toFixed(2));
}

export function calculateFireFenceParam(map, feature, setFireFenceParams) {
  const groupeLineDist = getGroupedLineDistance(map, feature);

  const waterVolume = calculateWaterVolume(feature, groupeLineDist);
  const perimeterLength = calculatePolygonLength(feature);
  const runningTime = calculateRunningTime(groupeLineDist);
  const successProbability = calculateSuccessProbability(map, feature);

  setFireFenceParams(prev => ({
    ...prev,
    waterVolume: Math.round(waterVolume),
    perimeterLength: Math.round(perimeterLength),
    runningTime,
    successProbability
  }));
};

export function calculatePolygonArea(feature) {
  return turf.area(feature);
}

function calculateGroupedLineDistance(groupedLineDistance) {
  return vegetationAreas.reduce((acc, obj) => {
    if (Object.prototype.hasOwnProperty.call(groupedLineDistance, obj.text)) {
      acc[obj.text] = groupedLineDistance[obj.text];
    }
    return acc;
  }, {});
}

function calculatePercentage(feature, groupedLineDistance) {
  const distance = calculatePolygonLength(feature);

  return (() => {
    const p = {};
    Object.entries(groupedLineDistance).forEach(([k, v]) => p[k] = Math.round((v / distance) * 100));
    return p;
  })();
}

export function calculateMaxAssetRisk() {
  try {
    const feats = window.draw?.getAll()?.features || [];
    const risks = feats
      .filter(f =>
        (f.properties?.assetContour || f.properties?.isBuilding) &&
        (f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon')
      )
      .map(f => String(f.properties?.riskClass || '').toLowerCase())
      .filter(r => r === 'low' || r === 'elevated' || r === 'high');

    if (!risks.length) return null;

    let max = 'low';
    for (const r of risks) {
      if (RISK_ORDER[r] >= RISK_ORDER[max]) max = r;
    }
    return { riskMessage: RISK_MESSAGE[max] };
  } catch {
    return null;
  }
}

export function calculateInitialAsessmentParams(map, feature, setInitialAsessmentParams) {
  const groupeLineDist = getGroupedLineDistance(map, feature);

  const perimeterLength = calculatePolygonLength(feature);
  const perimeterArea = calculatePolygonArea(feature);
  const groupedLineDistance = calculateGroupedLineDistance(groupeLineDist);
  const percentage = calculatePercentage(feature, groupeLineDist);

  setInitialAsessmentParams(prev => ({
    ...prev,
    perimeterLength: Math.round(perimeterLength),
    perimeterArea: Math.round(perimeterArea),
    groupedLineDistance,
    percentage,
  }));
}

export function calculateAnalysedParams(map, feature, setAnalysedParams) {
  const groupeLineDist = getGroupedLineDistance(map, feature);

  const perimeterLength = calculatePolygonLength(feature);
  const runningTime = calculateRunningTime(groupeLineDist);
  const successProbability = calculateSuccessProbability(map, feature);
  const groupedLineDistance = calculateGroupedLineDistance(groupeLineDist);
  const percentage = calculatePercentage(feature, groupeLineDist);

  setAnalysedParams(prev => ({
    ...prev,
    perimeterLength: Math.round(perimeterLength),
    runningTime,
    successProbability,
    groupedLineDistance,
    percentage
  }));
}